// AE11 — the engine A/B comparison, a SECTION of the Assistant page (UX17).
//
// It used to be its own nav row, one line under Assistant, showing engines that only
// mean anything in the assistant's terms — which default a new conversation binds to,
// which engine served which turns. Two rows for one subject is one row too many, so
// this exports <EnginesSection/> and /engines redirects to /assistant.
//
// The engine A/B comparison itself is unchanged. Two data sources, merged
// by engine name:
//   * GET /assistant-api/v1/admin/engines — the registered engines, their default flag, live health,
//     and AEP capabilities (admin-gated; carries the caller's bearer).
//   * GET /assistant-api/v1/metrics/summary — the per-engine metrics rollup (`summary.engines`):
//     TTFT p50/p95, whole-turn p50/p95, outcomes, tool-calls/turn (AF6 engine label, AE11 split).
// A control retargets the RUNTIME default engine without a restart (new conversations bind to it).
//
// GAPS (rendered as "—" with a footnote): the metrics layer does NOT tag subchat lifecycle or
// deep-turn cost counters (Codex/OpenClaw escalation calls) per engine, so those columns show "—"
// until the assistant emits them per-engine. See the report note in the epic reports/.

import { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { Loading, ErrorState, Badge } from '../components/ui';
import type {
  AssistantAdminEngines,
  AssistantEngineInfo,
  AssistantEngineStatRow,
  AssistantMetricsSummary,
} from '../api/schemas';

const WINDOW_S = 86_400; // 24h — mirror the Assistant tab window.
const REFRESH_MS = 15_000;

interface EngineRow {
  info: AssistantEngineInfo;
  stats: AssistantEngineStatRow | null;
}

// Merge the admin engine list (source of truth for WHICH engines exist + health/caps/default) with
// the per-engine metrics rollup (may be absent for an engine with no turns in the window). Exported
// for the unit test.
export function mergeEngineRows(
  admin: AssistantAdminEngines,
  summary: AssistantMetricsSummary,
): EngineRow[] {
  const statsByEngine = new Map(summary.engines.map((e) => [e.engine, e]));
  return admin.engines.map((info) => ({
    info,
    stats: statsByEngine.get(info.name) ?? null,
  }));
}

// The capability badges an engine advertises (only the ones that are ON). Exported for the test.
export function capabilityBadges(info: AssistantEngineInfo): string[] {
  const caps = info.capabilities;
  const on: string[] = [];
  if (caps.subchats) on.push('subchats');
  if (caps.memory) on.push('memory');
  if (caps.thinking) on.push('thinking');
  return on;
}

function ms(v: number | undefined): string {
  return v && v > 0 ? `${Math.round(v)}ms` : '—';
}

export function EnginesSection() {
  const admin = useResource(() => api.getAdminEngines(), []);
  const metrics = useResource(() => api.assistantMetricsSummary(WINDOW_S), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = setInterval(() => {
      admin.reload(true);
      metrics.reload(true);
    }, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDefault = useCallback(
    async (engine: string) => {
      setBusy(engine);
      setErr(null);
      try {
        await api.setDefaultEngine(engine);
        await admin.reload(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'set-default failed');
      } finally {
        setBusy(null);
      }
    },
    [admin],
  );

  if (admin.loading || metrics.loading) return <Loading what="Loading engines…" />;
  if (admin.error || !admin.data)
    return <ErrorState message={admin.error ?? 'no data'} onRetry={admin.reload} />;

  const summary = metrics.data ?? ({ engines: [] } as unknown as AssistantMetricsSummary);
  const rows = mergeEngineRows(admin.data, summary);
  const currentDefault = admin.data.default;

  return (
    <>
      <div className="muted-row small">
        A/B comparison · default <Badge tone="ok">{currentDefault}</Badge> · {rows.length} registered
      </div>

      {err && <div className="muted small" role="alert">Action failed: {err}</div>}

      {/* Default-engine control + health/capabilities per engine. */}
      <section className="card">
        <header className="card-head">
          <h2>Registered engines</h2>
          <span className="muted small">set default = new conversations bind to it (no restart)</span>
        </header>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Engine</th>
                <th>Kind</th>
                <th>Health</th>
                <th>AEP</th>
                <th>Capabilities</th>
                <th>Default</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ info }) => {
                const healthy = info.health.ok;
                const caps = capabilityBadges(info);
                const rowBusy = busy === info.name;
                return (
                  <tr key={info.name}>
                    <td>
                      <div>{info.name}</div>
                      {info.base_url && <div className="muted small">{info.base_url}</div>}
                    </td>
                    <td><Badge tone={info.kind === 'builtin' ? 'muted' : 'info'}>{info.kind}</Badge></td>
                    <td><Badge tone={healthy ? 'ok' : 'bad'}>{healthy ? 'healthy' : 'down'}</Badge></td>
                    <td className="muted small">{info.capabilities.version || '—'}</td>
                    <td>
                      {caps.length === 0 ? (
                        <span className="muted small">—</span>
                      ) : (
                        caps.map((c) => (
                          <span key={c} style={{ marginRight: 4 }}>
                            <Badge tone="info">{c}</Badge>
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      {info.is_default ? (
                        <Badge tone="ok">default</Badge>
                      ) : (
                        <button
                          className="btn small"
                          disabled={rowBusy || !healthy}
                          title={healthy ? '' : 'engine is down'}
                          onClick={() => setDefault(info.name)}
                        >
                          Set default
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-engine metric comparison (AF6 engine label, AE11 split). */}
      <section className="card">
        <header className="card-head">
          <h2>Per-engine metrics</h2>
          <span className="muted small">
            window {Math.round(summary.window_seconds / 3600)}h · from /metrics/summary
          </span>
        </header>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Engine</th>
                <th style={{ textAlign: 'right' }}>Turns</th>
                <th style={{ textAlign: 'right' }}>TTFT p50</th>
                <th style={{ textAlign: 'right' }}>TTFT p95</th>
                <th style={{ textAlign: 'right' }}>Turn p50</th>
                <th style={{ textAlign: 'right' }}>Turn p95</th>
                <th style={{ textAlign: 'right' }}>ok</th>
                <th style={{ textAlign: 'right' }}>escalated</th>
                <th style={{ textAlign: 'right' }}>degraded</th>
                <th style={{ textAlign: 'right' }}>cancelled</th>
                <th style={{ textAlign: 'right' }}>tools/turn</th>
                {/* GAP columns — not emitted per-engine yet. */}
                <th style={{ textAlign: 'right' }}>subchats*</th>
                <th style={{ textAlign: 'right' }}>deep cost*</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ info, stats }) => {
                const o = stats?.outcomes ?? {};
                const d = stats?.durations_ms;
                return (
                  <tr key={info.name}>
                    <td>
                      {info.name}
                      {info.is_default && <> <Badge tone="ok">default</Badge></>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{stats?.turns ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{ms(d?.ttft.p50_ms)}</td>
                    <td style={{ textAlign: 'right' }}>{ms(d?.ttft.p95_ms)}</td>
                    <td style={{ textAlign: 'right' }}>{ms(d?.turn.p50_ms)}</td>
                    <td style={{ textAlign: 'right' }}>{ms(d?.turn.p95_ms)}</td>
                    <td style={{ textAlign: 'right' }}>{o.local ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{o.escalated ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{o.degraded ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{o.cancelled ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>
                      {stats ? stats.tool_iterations.avg.toFixed(2) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }} className="muted">—</td>
                    <td style={{ textAlign: 'right' }} className="muted">—</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="muted small" style={{ marginTop: 8 }}>
            * subchat lifecycle and deep-turn cost counters (Codex/OpenClaw escalation calls) are not
            yet tagged by engine in the assistant metrics — shown as “—” until per-engine emission
            lands. `ok` = local (non-escalated) outcome.
          </div>
        </div>
      </section>
    </>
  );
}
